// NOTE: mutators from final-form-arrays are buggy, hence the re-implementation here.

import { MutableState, Tools } from 'final-form';

type ArrayInsertParams<FormValues, K extends keyof FormValues & string> = [K, number, FormValues[K]];
type ArrayPushUnshiftParams<FormValues, K extends keyof FormValues & string> = [K, FormValues[K]];
type ArrayRemoveParams<FormValues, K extends keyof FormValues & string> = [K, number];
type ArrayPopParams<FormValues, K extends keyof FormValues & string> = [K];

export const insert = function insert<
  FormValues,
  K extends keyof FormValues & string,
  InitialFormValues = Partial<FormValues>,
>(
  [name, index, value]: ArrayInsertParams<FormValues, K>,
  state: MutableState<FormValues, InitialFormValues>,
  tools: Tools<FormValues, InitialFormValues>,
) {
  tools.changeValue(state, name, (array: Array<FormValues[K]>) => {
    const copy = ([] as Array<FormValues[K]>).concat(array || []);
    copy.splice(index, 0, value);
    return copy;
  });
};

export const unshift = function unshift<
  FormValues,
  K extends keyof FormValues & string,
  InitialFormValues = Partial<FormValues>,
>(
  [name, value]: ArrayPushUnshiftParams<FormValues, K>,
  state: MutableState<FormValues, InitialFormValues>,
  tools: Tools<FormValues, InitialFormValues>,
) {
  return insert([name, 0, value], state, tools);
};

export const remove = function remove<
  FormValues,
  K extends keyof FormValues & string,
  InitialFormValues = Partial<FormValues>,
>(
  [name, index]: ArrayRemoveParams<FormValues, K>,
  state: MutableState<FormValues, InitialFormValues>,
  tools: Tools<FormValues, InitialFormValues>,
) {
  tools.changeValue(state, name, array => {
    if (!array) {
      return array;
    }

    const copy = [].concat(array);
    copy.splice(index, 1);
    return copy.length > 0 ? copy : undefined;
  });
};

export const pop = function pop<
  FormValues,
  K extends keyof FormValues & string,
  InitialFormValues = Partial<FormValues>,
>(
  [name]: ArrayPopParams<FormValues, K>,
  state: MutableState<FormValues, InitialFormValues>,
  tools: Tools<FormValues, InitialFormValues>,
) {
  const array = tools.getIn(state.formState.values, name);
  return array && array.length > 0 ? remove([name, array.length - 1], state, tools) : undefined;
};

export const push = function push<
  FormValues,
  K extends keyof FormValues & string,
  InitialFormValues = Partial<FormValues>,
>(
  [name, value]: ArrayPushUnshiftParams<FormValues, K>,
  state: MutableState<FormValues, InitialFormValues>,
  tools: Tools<FormValues, InitialFormValues>,
) {
  tools.changeValue(state, name, array => {
    return array ? ([] as Array<FormValues[K]>).concat(array, [value]) : [value];
  });
};

export default { insert, pop, push, remove, unshift };
